import { useAuth } from '../../context/AuthContext';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import MobileBackButton from '../../components/MobileBackButton';

export default function AdvancedFeaturesPage() {
  const { isAuthenticated, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      router.push({
        pathname: '/login',
        query: { redirect: router.asPath }
      });
    }
  }, [isAuthenticated, loading, router]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="flex-1 p-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center mb-6">
          <MobileBackButton />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Advanced Features</h1>
        </div>

        {/* Introduction */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-500 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Professional-Grade Features</h3>
              <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                Master advanced features including SSML support, batch processing, real-time text highlighting,
                and custom pronunciation dictionaries for professional content creation.
              </p>
            </div>
          </div>
        </div>

        {/* SSML Support */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              SSML Markup for Fine Control
            </h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Speech Synthesis Markup Language (SSML) gives you precise control over how your text is spoken,
              including emphasis, pauses, pitch, speed, and pronunciation.
            </p>

            <div className="space-y-6">
              {/* Emphasis */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  📣 Emphasis & Strength
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Control word emphasis with three levels: strong, moderate, and reduced.
                </p>
                <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-md font-mono text-xs mb-2">
                  {'<emphasis level="strong">Anpil</emphasis> moun te la.'}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Result: "<strong>MANY</strong> people were there" (strong emphasis on "many")
                </p>
              </div>

              {/* Pauses */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  ⏸️ Custom Pauses
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Insert precise pauses for dramatic effect or natural phrasing.
                </p>
                <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-md font-mono text-xs mb-2">
                  {'Bonjou<break time="500ms"/> kijan ou ye?'}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Result: "Hello [500ms pause] how are you?"
                </p>
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  <strong>Pause Options:</strong> x-weak, weak, medium, strong, x-strong, or exact time (100ms-10s)
                </div>
              </div>

              {/* Prosody */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  🎵 Prosody Control
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Adjust pitch, rate, and volume for specific text sections.
                </p>
                <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Pitch</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody pitch="+20%">Vwa wo</prosody>'}
                    <span className="text-gray-500 ml-2">(Higher pitch)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody pitch="-20%">Vwa ba</prosody>'}
                    <span className="text-gray-500 ml-2">(Lower pitch)</span>
                  </div>
                </div>

                <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Speed</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody rate="x-slow">Pale trè dousman</prosody>'}
                    <span className="text-gray-500 ml-2">(50%)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody rate="slow">Pale dousman</prosody>'}
                    <span className="text-gray-500 ml-2">(75%)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody rate="fast">Pale vit</prosody>'}
                    <span className="text-gray-500 ml-2">(125%)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody rate="x-fast">Pale trè vit</prosody>'}
                    <span className="text-gray-500 ml-2">(150%)</span>
                  </div>
                </div>

                <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Volume</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody volume="loud">FÒ!</prosody>'}
                    <span className="text-gray-500 ml-2">(Louder)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody volume="soft">Dousman</prosody>'}
                    <span className="text-gray-500 ml-2">(Quieter)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody volume="silent">...</prosody>'}
                    <span className="text-gray-500 ml-2">(Muted)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody volume="120%">Pi fò</prosody>'}
                    <span className="text-gray-500 ml-2">(Absolute %)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody volume="-20%">Pi ba</prosody>'}
                    <span className="text-gray-500 ml-2">(Relative %)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<prosody volume="+3db">Pi fò</prosody>'}
                    <span className="text-gray-500 ml-2">(Decibel)</span>
                  </div>
                </div>
              </div>

              {/* Say-As */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  🔢 Say-As Interpretation
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Control how numbers, dates, times, and special content are spoken.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="cardinal">100</say-as>'}
                    <span className="text-gray-500 ml-2">(san)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="ordinal">1</say-as>'}
                    <span className="text-gray-500 ml-2">(premye)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="date" format="dmy">15/01/2024</say-as>'}
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="time">3:30pm</say-as>'}
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="telephone">555-1234</say-as>'}
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="bible-verses">Jean 1:10</say-as>'}
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="spell-out">CAP</say-as>'}
                    <span className="text-gray-500 ml-2">(C-A-P)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="digits">123</say-as>'}
                    <span className="text-gray-500 ml-2">(1-2-3)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="characters">ABC</say-as>'}
                    <span className="text-gray-500 ml-2">(A-B-C)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="url">favorite.website.com</say-as>'}
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="url" detail="300ms">favorite.website.com</say-as>'}
                    <span className="text-gray-500 ml-2">(custom delay)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'<say-as interpret-as="email">jean.jacques@google.com</say-as>'}
                  </div>
                </div>
                <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-3 rounded-r-md">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>URL Pronunciation Tip:</strong> The optional <code className="px-1 py-0.5 bg-white dark:bg-gray-800 rounded">detail</code> attribute
                    specifies the delay between URL components (e.g., between "favorite", "dot", "website"). If omitted,
                    the default delay is 500ms. Words like "favorite" and "website" in a URL can be added
                    to your personal dictionary with phonetic or IPA values to assist pronunciation. Common TLDs
                    (e.g., .com, .org, .net) already exist in the dictionary. Uncommon TLDs can be added to your
                    personal dictionary as well.
                  </p>
                </div>
                <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-3 rounded-r-md">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>Email Pronunciation Tip:</strong> The <code className="px-1 py-0.5 bg-white dark:bg-gray-800 rounded">email</code> type
                    reads email addresses naturally, spelling out the username, speaking "at", and pronouncing the domain.
                    For example, "jean.jacques@google.com" is read as "jean dot jacques at google dot com".
                    Add uncommon domain names to your personal dictionary for better pronunciation.
                  </p>
                </div>
              </div>
            </div>

            {/* How to Use SSML */}
            <div className="mt-6 p-6 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/20 rounded-lg border border-green-200 dark:border-green-700">
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-4">🚀 How to Use SSML</h3>
              <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                <p>
                  <strong>Option 1:</strong> Type SSML tags directly into your text editor on the TTS page.
                  Example: Type <code className="px-2 py-1 bg-white dark:bg-gray-800 rounded text-xs">Bonjou&lt;break time="1s"/&gt; kijan ou ye?</code>
                </p>
                <p>
                  <strong>Option 2:</strong> Click the "SSML Controls" button below the text editor to open the visual toolbar and insert tags with one click.
                </p>
                <p className="text-xs text-green-800 dark:text-green-200 pt-2 border-t border-green-300 dark:border-green-700">
                  <strong>✅ Status:</strong> SSML is fully implemented and tested! All tags (break, emphasis, prosody, say-as) are properly preserved and sent to Infer PathC for audio synthesis.
                </p>
              </div>
            </div>

            <div className="mt-6">
              <Link href="/developer/api-reference" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium">
                View Full SSML Documentation →
              </Link>
            </div>
          </div>
        </div>

        {/* Creole Studio Prompt Steering */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              🎬 Creole Studio Steering (Director's Notes + Inline Tags)
            </h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Creole Studio voices are prompt-steerable — instead of SSML markup, you shape the read with
              two paired surfaces: <strong>Director's Notes</strong> for longer prose direction, and
              <strong> Studio Controls</strong> for short inline delivery cues in square brackets. Only
              Studio voices interpret these; Creole Regional voices use SSML instead.
            </p>

            <div className="space-y-6">
              {/* Director's Notes */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  🎬 Director's Notes
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  A <strong>Use Case</strong> preset sets the baseline tone (Conversational,
                  Storytelling, News Read, Sportscaster, Sermon, and more). Freeform
                  <strong> Notes</strong> layer on top and refine — if the two conflict, the notes win.
                  Both are optional; use whichever feels natural.
                </p>
                <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-md text-xs mb-2">
                  <strong>Notes example:</strong> "Speak slowly, like reading a bedtime story.
                  Sound warm and reassuring."
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Notes are capped at 500 characters. Longer instructions probably belong here
                  rather than inline in the transcript.
                </p>
              </div>

              {/* Studio Controls / inline tags */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  🏷️ Studio Controls — Inline Tags
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Click the <strong>Studio Controls</strong> panel below the transcript to insert
                  short delivery cues at the cursor. Tags are grouped into 5 categories
                  (Pauses, Emotion, Delivery, Speed, Sounds), covering the full set of
                  supported inline cues. Place a tag before the words it should affect.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'[whispers] Sekrè.'}
                    <span className="text-gray-500 ml-2">(whispered)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'Bonjou [short pause] kijan ou ye?'}
                    <span className="text-gray-500 ml-2">(brief pause)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'[laughs] Sa dwòl!'}
                    <span className="text-gray-500 ml-2">(laughter, then text)</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md font-mono text-xs">
                    {'[excited] Nou pral nan plaj la!'}
                    <span className="text-gray-500 ml-2">(excited tone)</span>
                  </div>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
                  <p><strong>Categories:</strong></p>
                  <ul className="list-disc ml-5 space-y-0.5">
                    <li><strong>Pauses (3)</strong>: short pause, medium pause, long pause</li>
                    <li><strong>Emotion (16)</strong>: amazed, calm, cheerful, curious, excited, happy, sad, scared, serious, tired, urgent, etc.</li>
                    <li><strong>Delivery (8)</strong>: whispers, shouting, sarcastic, mischievously, reluctantly, robotic, etc.</li>
                    <li><strong>Speed (5)</strong>: very slow, slow, fast, very fast, extremely fast</li>
                    <li><strong>Sounds (8)</strong>: laughs, giggles, sighs, gasp, cough, uhm — replaced by an actual sound in the audio</li>
                  </ul>
                </div>
              </div>

              {/* Review modal */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  🧐 Automatic Tag Review
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Every time you press <strong>Generate Speech</strong>, the transcript is
                  scanned for bracketed content. Anything that isn't on the supported-tag list —
                  or that looks like prose (long, punctuated, or many-worded) — opens a review
                  modal. For each flagged bracket you can:
                </p>
                <ul className="list-disc ml-5 text-sm text-gray-600 dark:text-gray-300 space-y-1 mb-3">
                  <li><strong>Keep as-is</strong> — leave it in the transcript (use for tags you know work but aren't on the shortlist)</li>
                  <li><strong>Delete</strong> — remove the bracket entirely</li>
                  <li><strong>Move to Director's Notes</strong> — pull the content out of the transcript and append it to Notes</li>
                </ul>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Director's Notes is visible and editable inside the modal, updating live as you
                  toggle Move. Choose <strong>Apply</strong> to commit changes and keep editing, or
                  <strong> Apply &amp; Generate</strong> to commit and submit immediately.
                </p>
              </div>
            </div>

            {/* Practical guidance */}
            <div className="mt-6 p-6 bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/20 rounded-lg border border-orange-200 dark:border-orange-700">
              <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-4">💡 Which surface to use?</h3>
              <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <p>
                  <strong>Short delivery cue on one phrase</strong> ("say this next bit softly",
                  "insert a pause here", "play a laugh") → inline tag from Studio Controls.
                </p>
                <p>
                  <strong>Broad direction across the whole read</strong> ("sound formal", "like a
                  bedtime story", "with a French accent throughout") → Director's Notes.
                </p>
                <p>
                  <strong>Both at once</strong> is fine — Notes set the ambient tone, tags punctuate
                  specific moments. If in doubt, start with Notes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Streaming Playback */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Streaming Playback</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Hear your audio start playing within about a second of clicking <strong>Generate Speech</strong>,
              instead of waiting for the whole clip to render. Streaming is one of two Processing
              Modes (Standard / Streaming) and works on <strong>Creole Regional</strong> and
              <strong> Creole Studio Pro</strong> voices for inputs of any length — long text is
              automatically split into ≤600-character phrases that stream sequentially through
              one continuous audio player.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/20 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-3">🎚️ How to Turn It On</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>1. Pick a Creole Regional or Creole Studio Pro voice</li>
                  <li>2. In the <strong>Processing Mode</strong> picker, click the <strong>🎙️ Streaming</strong> button</li>
                  <li>3. Type or paste your text — any length works; long inputs are auto-split into phrases</li>
                  <li>4. Click <strong>🎙️ Generate Streaming Speech</strong> — audio starts playing as soon as the first chunk arrives</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/20 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">⚡ What You Get</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• First audio in about 1 second (vs. 3–10 seconds standard)</li>
                  <li>• <strong>Unlimited input length</strong> — long text auto-split at sentence boundaries</li>
                  <li>• <strong>Cost confirmation modal</strong> on long inputs (&gt;600 chars) showing phrase count + total credits before you commit</li>
                  <li>• Live <strong>Stop streaming</strong> button — cancels the in-progress phrase for a full refund; finished phrases stay in your History</li>
                  <li>• Final audio still saved to History (one row per batch) for replay, export, and subtitles</li>
                  <li>• <strong>1.5x credit cost</strong> — applies to every phrase in a batched submission</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/20 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-3">⚠️ When It's Disabled</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Creole Studio (non-Pro) voices — Streaming requires the Studio Pro tier; the picker greys it out with a tooltip</li>
                  <li>• Custom Voices — your cloned voice can only be applied to the full rendered audio, not incrementally; the picker greys it out</li>
                </ul>
                <p className="text-xs text-orange-900 dark:text-orange-200 mt-2">
                  Hover the disabled Streaming button to see the specific reason; the picker auto-reverts to Standard for any combo that can't stream.
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/20 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-3">📜 How Long Inputs Stream</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Text is split at sentence boundaries (<code>. ! ?</code>), falling back to clauses (<code>, ;</code>), with a hard cut at 600 chars only as a last resort.</li>
                  <li>• Each phrase becomes its own streaming job sharing a <strong>batch_id</strong>; History groups them into ONE row showing <em>"Phrase 3 of 7 streaming"</em>.</li>
                  <li>• Audio plays gaplessly via a shared Web Audio player — phrase boundaries land at natural sentence pauses (~0.7 s gap).</li>
                  <li>• Cancelling mid-batch: the current phrase is refunded in full; future phrases are never submitted and never charged. Phrases that already finished are kept and billed normally.</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/40 border-l-4 border-purple-500 p-4 rounded-r-md">
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">Tip</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                If you stop the stream mid-playback, the in-progress phrase is cancelled
                and its credits are refunded in full — there's no playable audio_url left
                behind for that phrase. Closing the browser tab also counts as Stop. Once
                the green History row appears, the audio (or the next finished phrase of
                a batch) has been delivered and is yours; cancelling at that point is a
                no-op (no refund).
              </p>
            </div>
          </div>
        </div>

        {/* Batch Processing */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Batch Document Processing</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Process multiple documents or long texts efficiently with our batch processing system.
              Perfect for converting books, articles, or educational materials.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/20 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">📚 Document Upload</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Upload .txt, .docx, .pdf files</li>
                  <li>• Up to 100,000 characters per batch</li>
                  <li>• Automatic chapter detection</li>
                  <li>• Preserves document structure</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/20 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-3">⚡ Parallel Processing</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Process multiple segments simultaneously</li>
                  <li>• 10x faster than sequential processing</li>
                  <li>• Priority queue for Pro+ plans</li>
                  <li>• Progress tracking dashboard</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/20 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-3">🔔 Webhook Notifications</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Real-time completion alerts</li>
                  <li>• Email notifications</li>
                  <li>• Custom webhook endpoints</li>
                  <li>• Batch status monitoring</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/20 p-5 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-3">💾 Bulk Download</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Download all files as ZIP</li>
                  <li>• Individual file downloads</li>
                  <li>• Multiple format exports</li>
                  <li>• Organized by chapter/section</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">💡 Pro Tip: Optimal Batch Size</h4>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                For best results, split very large documents (&gt;50,000 characters) into chapters or sections.
                This enables better parallel processing and easier management of output files.
              </p>
            </div>
          </div>
        </div>

        {/* Real-Time Highlighting */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Real-Time Text Highlighting</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Our innovative real-time highlighting feature syncs text with audio playback, perfect for literacy
              programs, language learning, and accessibility applications.
            </p>

            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 rounded-lg p-6 text-white">
                <h3 className="text-xl font-semibold mb-3">How It Works</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white/10 p-4 rounded-lg">
                    <div className="text-3xl mb-2">1️⃣</div>
                    <h4 className="font-medium mb-1">Word Timing</h4>
                    <p className="text-sm text-blue-100">Audio includes precise timestamps for each word</p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-lg">
                    <div className="text-3xl mb-2">2️⃣</div>
                    <h4 className="font-medium mb-1">Synchronized Display</h4>
                    <p className="text-sm text-blue-100">Text highlights in real-time as audio plays</p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-lg">
                    <div className="text-3xl mb-2">3️⃣</div>
                    <h4 className="font-medium mb-1">Interactive Playback</h4>
                    <p className="text-sm text-blue-100">Click any word to jump to that point</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-5 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-3">📚 Educational Benefits</h3>
                  <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                    <li>• Improves reading comprehension</li>
                    <li>• Supports literacy development</li>
                    <li>• Helps language learners</li>
                    <li>• Engages visual and auditory learners</li>
                    <li>• Perfect for dyslexia support</li>
                  </ul>
                </div>

                <div className="border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-5 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-3">♿ Accessibility Features</h3>
                  <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                    <li>• Screen reader compatible</li>
                    <li>• Adjustable highlight colors</li>
                    <li>• Customizable font sizes</li>
                    <li>• High-contrast mode support</li>
                    <li>• Keyboard navigation</li>
                  </ul>
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">📱 Export Options</h4>
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Download highlighting data as SRT subtitle files for use in video projects, e-learning platforms,
                  or custom applications. Full API support available for developers.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Pronunciation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Custom Pronunciation Dictionaries</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Create custom pronunciation rules for names, technical terms, acronyms, and regional expressions
              to ensure perfect pronunciation every time.
            </p>

            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">👤 Personal Names</h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                    Ensure names are pronounced correctly
                  </p>
                  <div className="bg-white dark:bg-gray-800 p-2 rounded text-xs font-mono">
                    Jean-Baptiste → Jan Batis
                  </div>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">🏢 Company Names</h4>
                  <p className="text-sm text-green-800 dark:text-green-200 mb-2">
                    Brand names with specific pronunciation
                  </p>
                  <div className="bg-white dark:bg-gray-800 p-2 rounded text-xs font-mono">
                    Nike → Nayke
                  </div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-2">🔤 Acronyms</h4>
                  <p className="text-sm text-purple-800 dark:text-purple-200 mb-2">
                    Define how acronyms should be spoken
                  </p>
                  <div className="bg-white dark:bg-gray-800 p-2 rounded text-xs font-mono">
                    UNESCO → Yunesko
                  </div>
                </div>
              </div>

              <div className="bg-gray-100 dark:bg-gray-700 p-5 rounded-lg">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Creating a Pronunciation Dictionary</h3>
                <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li className="flex items-start">
                    <span className="font-bold mr-2 text-blue-600 dark:text-blue-400">1.</span>
                    <span>Navigate to "Pronunciation Dictionary" in your account settings</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold mr-2 text-blue-600 dark:text-blue-400">2.</span>
                    <span>Click "Add New Entry" and enter the word as it appears in text</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold mr-2 text-blue-600 dark:text-blue-400">3.</span>
                    <span>Provide the phonetic spelling in Haitian Creole</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold mr-2 text-blue-600 dark:text-blue-400">4.</span>
                    <span>Test the pronunciation with sample audio</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold mr-2 text-blue-600 dark:text-blue-400">5.</span>
                    <span>Save and apply to all future audio generations</span>
                  </li>
                </ol>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-r-lg">
                <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">✅ Best Practices</h4>
                <ul className="text-sm text-green-800 dark:text-green-200 space-y-1">
                  <li>• Use standard Haitian Creole orthography for phonetic spellings</li>
                  <li>• Create entries for frequently used terms in your content</li>
                  <li>• Test pronunciations with different voices</li>
                  <li>• Share dictionaries across team members (Business+ plans)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Smart Format */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Smart Format</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Smart Format automatically processes your text to improve how numbers, dates, acronyms, and other
              special content is spoken aloud. It ensures natural-sounding speech without manual SSML tagging.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Numbers & Dates</h4>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  "2024" is read as "de mil ven kat" instead of "two-zero-two-four".
                  Dates like "15/01/2024" are read naturally.
                </p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">Acronyms</h4>
                <p className="text-sm text-green-800 dark:text-green-200">
                  Common acronyms like "UN", "NGO", or "USA" are automatically spelled out
                  for clear pronunciation.
                </p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-2">Phone Numbers</h4>
                <p className="text-sm text-purple-800 dark:text-purple-200">
                  Phone numbers are read digit-by-digit for clarity, matching how people
                  naturally speak phone numbers.
                </p>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-orange-900 dark:text-orange-100 mb-2">Currency</h4>
                <p className="text-sm text-orange-800 dark:text-orange-200">
                  "$50" becomes "50 dola" - currency symbols are converted to their
                  spoken equivalents.
                </p>
              </div>
            </div>

            <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>How to enable:</strong> Smart Format is enabled by default. You can toggle it in
                <strong> Settings &gt; Text Processing</strong>. Processing is handled by Infer for optimal results.
              </p>
            </div>
          </div>
        </div>

        {/* Creolize French Words */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Creolize French Words</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Haitian Creole has many French-derived words. The Creolize feature automatically detects French words
              in your text and suggests their Creole equivalents for more authentic pronunciation.
            </p>

            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">How It Works</h4>
                <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
                  <li><strong>1.</strong> When you generate audio, the system scans your text for French words.</li>
                  <li><strong>2.</strong> A review modal appears showing detected French words with their Creole alternatives.</li>
                  <li><strong>3.</strong> You can accept, reject, or modify each suggestion before applying.</li>
                  <li><strong>4.</strong> The text is updated with your approved changes before synthesis.</li>
                </ol>
              </div>

              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>How to enable:</strong> Toggle Creolize French Words in <strong>Settings &gt; Text Processing</strong>.
                  The review modal ensures you always have control over which words are replaced.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Spellcheck on Generate */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Spellcheck on Generate</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Catch potential misspellings before generating audio. When enabled, spellcheck runs automatically
              when you click Generate and flags words not found in the Haitian Creole dictionary.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">Dictionary-Based</h4>
                <p className="text-sm text-green-800 dark:text-green-200">
                  Words are checked against our comprehensive Haitian Creole dictionary. Unknown words are highlighted
                  with spelling suggestions when available.
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Review Modal</h4>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  A modal shows all unknown words with suggestions. You can accept suggestions, ignore individual
                  words, or proceed with generation as-is.
                </p>
              </div>
            </div>

            <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>How to enable:</strong> Toggle Spellcheck on Generate in <strong>Settings &gt; Text Processing</strong>.
                This feature works alongside Smart Format and Creolize for comprehensive text preparation.
              </p>
            </div>
          </div>
        </div>

        {/* Express vs Standard Mode */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Express vs Standard Mode</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Choose between Standard and Express generation modes depending on your needs for speed vs. cost.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="border-2 border-blue-500 rounded-lg p-5 bg-blue-50 dark:bg-blue-900/20">
                <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-100 mb-3">Standard Mode</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li><strong>Processing:</strong> Asynchronous (queued)</li>
                  <li><strong>Credit Cost:</strong> 1x (standard rate)</li>
                  <li><strong>Character Limit:</strong> Full plan limit</li>
                  <li><strong>Best For:</strong> Regular usage, longer texts, batch processing</li>
                </ul>
              </div>

              <div className="border-2 border-green-500 rounded-lg p-5 bg-green-50 dark:bg-green-900/20">
                <h3 className="text-xl font-semibold text-green-900 dark:text-green-100 mb-3">Streaming Mode</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li><strong>Processing:</strong> First audio arrives in ~1 second; the rest streams in as it's generated</li>
                  <li><strong>Credit Cost:</strong> 1.5x (50% premium)</li>
                  <li><strong>Supported by:</strong> Creole Regional and Creole Studio Pro</li>
                  <li><strong>Character Limit:</strong> Any length — long text is auto-split into ≤600-char phrases that stream sequentially</li>
                  <li><strong>Best For:</strong> Quick previews, live playback, time-sensitive content</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-r-lg">
              <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Plan Availability</h4>
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Streaming mode is available on all paid plans (Learner and above). Free plan users can only use
                Standard mode. Switch between modes using the toggle on the TTS page.
              </p>
            </div>
          </div>
        </div>

        {/* API Integration */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Need API Access for Advanced Features?</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              All advanced features are available through our comprehensive REST API. Perfect for integrating
              CreoleCentric into your applications, workflows, or custom tools.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">API Features Include:</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700 dark:text-gray-300">Full SSML support in requests</span>
              </div>
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700 dark:text-gray-300">Batch processing endpoints</span>
              </div>
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700 dark:text-gray-300">Word-level timing data</span>
              </div>
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700 dark:text-gray-300">Pronunciation dictionary management</span>
              </div>
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700 dark:text-gray-300">Webhook callbacks</span>
              </div>
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700 dark:text-gray-300">Multiple output formats</span>
              </div>
            </div>

            <Link href="/developer/api-reference" className="inline-flex items-center px-6 py-3 bg-blue-600 dark:bg-blue-700 text-white rounded-lg font-medium hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors">
              View API Documentation
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 rounded-lg p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Ready to Use Advanced Features?</h2>
          <p className="mb-6 text-blue-100">
            Start using SSML, batch processing, and custom pronunciations today. All features are available
            in your account with no additional setup required.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Link href="/text-to-speech" className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors text-center">
              Start Creating
            </Link>
            <Link href="/docs/help-center" className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-400 transition-colors text-center">
              Get Help
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
